import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';

import { splitButtonSpotlightMessages } from './messages.ts';
import { useCwRSpotlightSeenStorage } from './useCwRSpotlightSeenStorage.tsx';
import { useRenderCwRSpotlight, type UseRenderCwRSpotlightArgs } from './useRenderCwRSpotlight.tsx';
import type { MenuItem } from '@atlassian/universal-create/types';

const MENU_ANCHOR_SELECTOR = '[data-testid="global-create-content-popover-menu--hidden"]';
const BUTTON_ANCHOR_SELECTOR = '[data-testid="uc-trigger-button-primary"]';

const SHARED_CHOREOGRAPHER_MESSAGE_ID = 'UniversalCreateCwRExperimentSpotlight';

type VariantConfig = Omit<
	UseRenderCwRSpotlightArgs,
	'isEnabled' | 'onCtaClick' | 'onClosed' | 'choreographerMessageId'
>;

const MENU_VARIANT_CONFIG: VariantConfig = {
	anchorSelector: MENU_ANCHOR_SELECTOR,
	messages: {
		title: splitButtonSpotlightMessages.commonTitle,
		bodyLine1: splitButtonSpotlightMessages.ucDropdownBodyFirst,
		bodyLine2: splitButtonSpotlightMessages.ucDropdownBodySecond,
		cta: splitButtonSpotlightMessages.commonConfirm,
	},
	placement: 'left-end',
	requireAnchorForStart: false,
};

const BUTTON_VARIANT_CONFIG: VariantConfig = {
	anchorSelector: BUTTON_ANCHOR_SELECTOR,
	messages: {
		title: splitButtonSpotlightMessages.commonTitle,
		bodyLine1: splitButtonSpotlightMessages.cwrModalBodyFirst,
		bodyLine2: splitButtonSpotlightMessages.cwrModalBodySecond,
		cta: splitButtonSpotlightMessages.commonCreateWithRovo,
	},
	placement: 'bottom-start',
	requireAnchorForStart: true,
};

type SpotlightVariant = 'button' | 'menu';

const SPOTLIGHT_VARIANT_CONFIGS: Record<SpotlightVariant, VariantConfig> = {
	button: BUTTON_VARIANT_CONFIG,
	menu: MENU_VARIANT_CONFIG,
};

export type UseCwRSpotlightOrchestratorArgs = {
	isMenuOpen: boolean;
	onCloseMenu?: () => void;
	onClick?: () => void;
	primaryActionItem?: MenuItem;
	enabled?: boolean;
};

export type UseCwRSpotlightOrchestratorResult = {
	onClick?: () => void;
	onCloseMenu: () => void;
	primaryActionItem?: MenuItem;
	spotlight: ReactNode;
};

export function useCwRSpotlightOrchestrator({
	isMenuOpen,
	onCloseMenu,
	onClick: dropdownOnClick,
	primaryActionItem,
	enabled = true,
}: UseCwRSpotlightOrchestratorArgs): UseCwRSpotlightOrchestratorResult {
	const { hasSeenSpotlight } = useCwRSpotlightSeenStorage();
	const [spotlightVariant, setSpotlightVariant] = useState<SpotlightVariant>('button');
	const [hasInteracted, setHasInteracted] = useState(false);

	const pendingVariantRef = useRef<SpotlightVariant | null>(null);
	const pendingOnClickRef = useRef<(() => void) | undefined>(undefined);
	const startSpotlightRef = useRef<() => boolean>(() => false);
	const stopSpotlightRef = useRef<() => void>(() => {});

	const flushPendingOnClick = useCallback(() => {
		const pending = pendingOnClickRef.current;
		pendingOnClickRef.current = undefined;
		pending?.();
	}, []);

	const spotlightVariantRef = useRef<SpotlightVariant>(spotlightVariant);
	spotlightVariantRef.current = spotlightVariant;

	const handleMenuClosed = useCallback(() => {
		const variantActiveOrPending =
			spotlightVariantRef.current === 'menu' || pendingVariantRef.current === 'menu';
		if (variantActiveOrPending) {
			stopSpotlightRef.current();
			pendingVariantRef.current = null;
		}
		onCloseMenu?.();
	}, [onCloseMenu]);

	const handleCtaClick = useCallback(() => {
		if (spotlightVariantRef.current === 'menu') {
			handleMenuClosed();
		}
	}, [handleMenuClosed]);

	const handleSpotlightClosed = useCallback(() => {
		if (spotlightVariantRef.current === 'button') {
			flushPendingOnClick();
		}
	}, [flushPendingOnClick]);

	const activeSpotlightConfig = SPOTLIGHT_VARIANT_CONFIGS[spotlightVariant];

	const { spotlight, startSpotlight, stopSpotlight } = useRenderCwRSpotlight({
		...activeSpotlightConfig,
		choreographerMessageId: SHARED_CHOREOGRAPHER_MESSAGE_ID,
		isEnabled: enabled && (spotlightVariant === 'button' || isMenuOpen),
		onCtaClick: handleCtaClick,
		onClosed: handleSpotlightClosed,
	});
	startSpotlightRef.current = startSpotlight;
	stopSpotlightRef.current = stopSpotlight;

	const scheduleSpotlightStart = useCallback(
		(variant: SpotlightVariant) => {
			pendingVariantRef.current = variant;

			if (spotlightVariantRef.current !== variant || !hasInteracted) {
				flushSync(() => {
					setHasInteracted(true);
					setSpotlightVariant(variant);
				});
			}

			const didStart = startSpotlightRef.current();
			if (!didStart) {
				pendingVariantRef.current = null;
				if (variant === 'button') {
					flushPendingOnClick();
				}
			}
		},
		[flushPendingOnClick, hasInteracted],
	);

	const interceptedDropdownOnClick = useCallback(() => {
		dropdownOnClick?.();

		if (hasSeenSpotlight || pendingVariantRef.current !== null) {
			return;
		}

		scheduleSpotlightStart('menu');
	}, [hasSeenSpotlight, dropdownOnClick, scheduleSpotlightStart]);

	const interceptedPrimaryActionItem = useMemo(() => {
		if (!primaryActionItem || hasSeenSpotlight) {
			return primaryActionItem;
		}

		const originalPrimaryOnClick = primaryActionItem.onClick;
		const interceptedPrimaryOnClick: MenuItem['onClick'] = (event) => {
			pendingOnClickRef.current = originalPrimaryOnClick
				? () => originalPrimaryOnClick(event)
				: undefined;
			scheduleSpotlightStart('button');
		};

		return { ...primaryActionItem, onClick: interceptedPrimaryOnClick };
	}, [hasSeenSpotlight, primaryActionItem, scheduleSpotlightStart]);

	if (!enabled) {
		return {
			onClick: dropdownOnClick,
			onCloseMenu: onCloseMenu ?? (() => {}),
			primaryActionItem,
			spotlight: null,
		};
	}

	return {
		onClick: hasSeenSpotlight ? dropdownOnClick : interceptedDropdownOnClick,
		onCloseMenu: handleMenuClosed,
		primaryActionItem: interceptedPrimaryActionItem,
		spotlight: hasInteracted ? spotlight : null,
	};
}
